import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { employerAPI } from '../../services/api';
import { FaBuilding, FaCheckCircle, FaTimesCircle, FaClock, FaArrowLeft, FaEye } from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ManageEmployers = () => {
    const [employers, setEmployers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadEmployers();
    }, [filter]);

    const loadEmployers = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (filter === 'pending') filters.is_approved = false;
            if (filter === 'approved') filters.is_approved = true;

            const response = await employerAPI.getAll(filters);
            const employersList = response.employers || response || [];
            setEmployers(employersList);
        } catch (err) {
            console.error('Failed to load employers:', err);
            toast.error('Failed to load employers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (employerId) => {
        const confirmed = await confirmAction('Approve this employer?');
        if (!confirmed) return;

        try {
            await employerAPI.approve(employerId);
            toast.success('Employer approved successfully!');
            loadEmployers();
        } catch (err) {
            console.error('Failed to approve employer:', err);
            toast.error('Failed to approve employer. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=employers" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Dashboard</span>
                </Link>

                <DashboardHeader
                    icon={FaBuilding}
                    title="Employer Management"
                    subtitle="Review and approve employer registrations"
                />

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex gap-4">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All Employers
                        </button>
                        <button
                            onClick={() => setFilter('pending')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'pending' ? 'bg-orange-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Pending Approval
                        </button>
                        <button
                            onClick={() => setFilter('approved')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'approved' ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Approved
                        </button>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : employers.length === 0 ? (
                    <EmptyState
                        icon={FaBuilding}
                        message="No employers found"
                    />
                ) : (
                    <div className="space-y-4">
                        {employers.map((employer) => (
                            <div key={employer.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <h3 className="text-lg font-semibold text-gray-900">{employer.company_name}</h3>
                                            <StatusBadge status={employer.is_approved ? 'approved' : 'pending'} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                            <div className="text-sm text-gray-600">
                                                <span className="font-medium">Email:</span> {employer.email}
                                            </div>
                                            {employer.company_description && (
                                                <div className="text-sm text-gray-600">
                                                    <span className="font-medium">Description:</span> {employer.company_description.substring(0, 100)}...
                                                </div>
                                            )}
                                        </div>
                                        {employer.compliance_status && (
                                            <div className="text-sm text-gray-600 mb-3">
                                                <span className="font-medium">Compliance:</span> 
                                                <StatusBadge status={employer.compliance_status} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <Link
                                            to={`/admin/employers/${employer.id}/review`}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <FaEye />
                                        </Link>
                                        {!employer.is_approved && (
                                            <button
                                                onClick={() => handleApprove(employer.id)}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                title="Approve"
                                            >
                                                <FaCheckCircle />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageEmployers;
